/**
 * Pure hit-testing helpers.
 *
 * Tokens are drawn at the center of their grid cell with radius = cellPx * 0.45.
 * Hit-test iterates tokens in reverse (topmost-rendered first) and returns the
 * first whose center is within radius of the click in *logical* (image-pixel)
 * coordinates. This keeps the math independent of viewport zoom/pan.
 */

import { gridCellCenter, screenToLogical } from './grid.js';
import type { Token, Viewport } from './types.js';

export const TOKEN_RADIUS_FACTOR = 0.45;

/**
 * Find the topmost token at the given screen position.
 * Returns null if no token is hit.
 */
export function tokenAtScreen(
  screenX: number,
  screenY: number,
  tokens: readonly Token[],
  cellPxValue: number,
  viewport: Viewport,
): Token | null {
  const { x, y } = screenToLogical(screenX, screenY, viewport);
  return tokenAtLogical(x, y, tokens, cellPxValue);
}

/** Same as tokenAtScreen but takes already-converted logical coords. */
export function tokenAtLogical(
  logicalX: number,
  logicalY: number,
  tokens: readonly Token[],
  cellPxValue: number,
): Token | null {
  if (cellPxValue <= 0) {
    throw new Error('cellPx must be > 0');
  }
  const radius = cellPxValue * TOKEN_RADIUS_FACTOR;
  // Iterate in reverse so the most-recently-drawn token wins.
  for (let i = tokens.length - 1; i >= 0; i--) {
    const t = tokens[i]!;
    const { cx, cy } = gridCellCenter(t.grid_x, t.grid_y, cellPxValue);
    if (Math.hypot(logicalX - cx, logicalY - cy) <= radius) {
      return t;
    }
  }
  return null;
}

/**
 * Fog-of-war contribution radius for a token, in logical pixels.
 * Only PC tokens contribute (NPCs do not reveal fog).
 */
export function fogRadiusForToken(token: Pick<Token, 'type' | 'visibility_ft'>, cellPxValue: number): number {
  if (token.type !== 'pc') return 0;
  // Visibility is in feet; one cell == 5 ft.
  return (token.visibility_ft / 5) * cellPxValue;
}
